import React, { useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  OptionsMenu,
  Icon,
  Tag,
  BaseIcon,
  Tooltip,
} from '@scale/scaleui';
import { DripsHook } from './useDrips';
import SAlert from 'react-s-alert';
import { sendTestEmail } from 'react-scale/corp/lib/api/growth';
import { PodGroup, EmailType, EmailRequest } from '@scale/shared-types';
import { useMutation } from 'react-query';

interface DripSetupProps {
  index: number;
  dripsHook: DripsHook;
  onEmailTemplateModalOpen: (index: number) => void;
  podGroup: PodGroup;
  emailType: EmailType;
  csvSubstitutions?: Record<string, string>;
  isViewOnly?: boolean;
}

// eslint-disable-next-line complexity
const DripSetup: React.FC<DripSetupProps> = ({
  index,
  dripsHook,
  onEmailTemplateModalOpen,
  podGroup,
  emailType,
  csvSubstitutions,
  isViewOnly = false,
}) => {
  const currentDrip = dripsHook.drips[index];
  const dripName = currentDrip?.name;
  const dripId = currentDrip?._id;

  const updateDrip = (key: string, value: number) => {
    const newDrips = dripsHook.drips.map((drip, i) => {
      if (i === index) {
        return { ...drip, [key]: value };
      }
      return drip;
    });
    dripsHook.setDrips(newDrips);
  };

  const pauseDrip = () => {
    if (!dripId) {
      SAlert.error(
        `Only saved ${emailType}s can be paused. You may remove this ${emailType} instead.`,
      );
      return;
    }
    const newDrips = dripsHook.drips.map((drip, i) => {
      if (i === index) {
        return drip.type === EmailType.Trigger
          ? { ...drip, active: false, percentage: 0 }
          : { ...drip, active: false };
      }
      return drip;
    });
    dripsHook.setDrips(newDrips);
  };

  const reactivateDrip = () => {
    if (!dripId || currentDrip.active) {
      SAlert.error(`Reactivation action not allowed on unsaved or active ${emailType}s.`);
      return;
    }
    const newDrips = dripsHook.drips.map((drip, i) => {
      if (i === index) {
        return { ...drip, active: true };
      }
      return drip;
    });
    dripsHook.setDrips(newDrips);
  };

  const copyDrip = () => {
    if (dripsHook.countDrips(emailType) >= 5) {
      SAlert.error(`You can only have up to 5 ${emailType}s per campaign.`);
      return;
    }
    dripsHook.setDrips([
      ...dripsHook.drips.slice(0, index + 1),
      { ...currentDrip, _id: undefined },
      ...dripsHook.drips.slice(index + 1),
    ]);
  };

  const testEmailTemplate = async () => {
    try {
      if (currentDrip.email) {
        const res = await sendTestEmail(
          currentDrip.email as EmailRequest,
          podGroup,
          csvSubstitutions,
          currentDrip.variableValues,
        );
        if (res.error) {
          throw new Error('Error sending test email.');
        }
        const newDrips = dripsHook.drips.map((drip, i) => {
          if (i === index) {
            return { ...drip, tested: true };
          }
          return drip;
        });
        dripsHook.setDrips(newDrips);
        SAlert.success(`Test email sent successfully to ${res.email}, please check your inbox.`, {
          position: 'top-right',
          timeout: 5000,
        });
      } else {
        SAlert.error('Please configure email template first.');
        return;
      }
    } catch (e) {
      if (e.response && e.response.status === 401) {
        SAlert.error('Your session has expired. Please log in again to access this tool.');
      } else {
        SAlert.error('Error sending test email. Please try again.', e);
      }
    }
  };

  const testEmailTemplateMutation = useMutation(testEmailTemplate);

  const activeTriggers = dripsHook.drips.filter(
    drip => drip.type === EmailType.Trigger && !drip.deletedAt && drip.active,
  );

  const calculatePercentage = (count: number) =>
    count >= 1 && count <= 3 ? Math.floor(100 / count) : 0;

  // Sets default percentages for triggers
  useEffect(() => {
    if (
      !currentDrip.emailDripCampaignId ||
      activeTriggers.reduce((sum, trigger) => sum + (trigger.percentage || 0), 0) !== 100
    ) {
      const percentage = calculatePercentage(activeTriggers.length);
      const newDrips = dripsHook.drips.map(drip => {
        if (drip.type === EmailType.Trigger && !drip.deletedAt && drip.active) {
          // Ternary handles 33%, 33%, 34% split for 3 triggers
          return {
            ...drip,
            percentage: activeTriggers.length === 3 && drip === activeTriggers[2] ? 34 : percentage,
          };
        }
        return drip;
      });
      dripsHook.setDrips(newDrips);
    }
  }, [activeTriggers.length]);

  return (
    <div className="flex flex-row items-center gap-2">
      {emailType === EmailType.Drip ? (
        <TextField
          disabled={!currentDrip?.active}
          className="w-[100px]"
          id="days"
          onChange={e => {
            if (Number(e.target.value) <= 0) {
              return;
            }
            updateDrip('daysInStage', Number(e.target.value));
          }}
          type="number"
          value={currentDrip?.daysInStage}
          leadingIcon="calendar-day"
        />
      ) : (
        <TextField
          disabled={!currentDrip?.active || activeTriggers.length === 1}
          className="w-[80px]"
          id="percentage"
          onChange={e => {
            if (Number(e.target.value) <= 0 || Number(e.target.value) > 100) {
              return;
            }
            updateDrip('percentage', Number(e.target.value));
          }}
          type="number"
          value={currentDrip?.percentage}
          trailingIcon="percent"
          hideSpinner
        />
      )}
      <div>
        <Typography.Paragraph2>
          {emailType === EmailType.Drip ? 'days later, send' : 'chance to send'}
        </Typography.Paragraph2>
      </div>

      {dripName && !currentDrip?.deletedAt && (
        <div
          className={`w-[400px] px-2 py-0.5 flex flex-row justify-start gap-2 rounded-lg items-center border border-neutral-300`}
        >
          <Icon
            icon="envelope"
            size="sm"
            variant={currentDrip?.active ? 'info' : 'neutral'}
            hasBackground={true}
            className=""
          />
          <Tooltip
            target={
              <div>
                <Typography.Paragraph2 className="truncate w-[220px]">
                  {dripName}
                </Typography.Paragraph2>
              </div>
            }
            side="bottom"
            text={dripName && dripId ? `${dripName} (${dripId})` : dripName || ''}
          />
          {!currentDrip?.tested && (
            <Tag className="w-45" variant="danger" icon={['far', 'circle-xmark']}>
              untested
            </Tag>
          )}
          {currentDrip?.tested && (
            <Tag
              className="w-45"
              variant={currentDrip?.active ? 'success' : 'neutral'}
              icon={['far', 'check-circle']}
            >
              tested
            </Tag>
          )}
          <Icon
            className="ml-auto"
            size="md"
            icon={isViewOnly ? ['fas', 'eye'] : ['fas', 'edit']}
            tooltip={isViewOnly ? 'View Email' : 'Edit Email'}
            variant="neutral"
            onClick={() => onEmailTemplateModalOpen(index)}
          />
        </div>
      )}

      {!dripName && (
        <div className="p-0.5 border border-neutral-300 rounded-lg  ">
          <Button
            className=" border-0 shadow-none"
            size="sm"
            variant="transparent"
            onClick={() => onEmailTemplateModalOpen(index)}
          >
            <BaseIcon edge="start" icon={['fas', 'plus']} />
            Configure Email Template
          </Button>
        </div>
      )}

      {dripName && (
        <Button
          size="lg"
          variant="secondary"
          onClick={() => testEmailTemplateMutation.mutate()}
          disabled={
            !currentDrip.email || !currentDrip?.active || testEmailTemplateMutation.isLoading
          }
        >
          {testEmailTemplateMutation.isLoading ? 'Sending...' : 'Send Test Email'}
        </Button>
      )}

      {!currentDrip?.active && (
        <Tag className="w-45" variant="neutral">
          paused
        </Tag>
      )}

      {!isViewOnly && (
        <OptionsMenu
          buttonIcon="ellipsis-h"
          icon="ellipsis-h"
          iconSize="sm"
          iconVariant="neutral"
          size="sm"
          direction="bottom-left"
          variant="neutral"
          menuItems={[
            {
              label: 'Copy Email',
              onClick: () => {
                copyDrip();
              },
            },
            {
              label: 'Delete Email',
              onClick: () => {
                dripsHook.deleteDrip(index);
              },
            },
            {
              label: 'Pause Email',
              onClick: () => {
                pauseDrip();
              },
            },
            {
              label: 'Reactivate Email',
              onClick: () => {
                reactivateDrip();
              },
            },
          ]}
        />
      )}
    </div>
  );
};

export default DripSetup;
